/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, KeyboardEvent } from 'react';

import { Input } from '@/components/ui/input';
import { useAutocompleteSuggestions } from '@/hooks/useAutocompleteSuggestions';
import { cn } from '@/lib/utils';
import { useSearchStore } from '@/store/SearchStore';

interface AutocompleteSearchProps {
  offset: 'start' | 'end';
  focused?: boolean;
}

export const AutocompleteSearch = (props: AutocompleteSearchProps) => {
  const { offset, focused } = props;
  const { locationStart, locationEnd, searching, setLocationSearch, locationSearch, searchIndex } = useSearchStore();

  const defaultValue = offset === 'start' ? locationStart?.displayName : locationEnd?.displayName;
  const [inputValue, setInputValue] = useState<string>(defaultValue ?? '');
  const { suggestions, filteredOutCount, totalCount } = useAutocompleteSuggestions(inputValue);
  const mounted = useRef<boolean>(false);

  useEffect(() => {
    setTimeout(() => (mounted.current = true), 0);
  }, []);

  useEffect(() => {
    if (inputValue !== defaultValue) {
      setInputValue(defaultValue ?? '');
    }
  }, [defaultValue]);

  useEffect(() => {
    // Update the location search whenever suggestions change
    // This ensures we always have the latest data from the search hook
    if (inputValue !== defaultValue && mounted.current) {
      setLocationSearch({ offset, suggestions, filteredOutCount, totalCount });
    }
  }, [suggestions, filteredOutCount, totalCount]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { setSearchIndex, searchIndex, locationSearch } = useSearchStore.getState();

    const currentIndex = searchIndex ?? -1;
    const totalItems = locationSearch?.suggestions?.length ?? 0;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSearchIndex(Math.min(currentIndex + 1, totalItems));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSearchIndex(Math.max(currentIndex - 1, 0));
    } else if (event.key === 'Enter') {
      document.querySelector<HTMLButtonElement>(`#suggestion-item-${currentIndex}`)?.click();
      event.preventDefault();
      setLocationSearch(undefined);
      setSearchIndex(undefined);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setInputValue((offset === 'start' ? locationStart : locationEnd)?.displayName ?? '');
      setLocationSearch(undefined);
      setSearchIndex(undefined);
    }
  };

  return (
    <Input
      value={inputValue}
      onInput={(event) => setInputValue((event.target as HTMLInputElement).value)}
      placeholder={`${offset.charAt(0).toUpperCase()}${offset.slice(1)} location`}
      onFocus={(event) => {
        setTimeout(() => event.target.select(), 100);
        // Only set location search if it's not already set for this offset
        if (locationSearch?.offset !== offset) {
          setLocationSearch({ offset, suggestions, filteredOutCount, totalCount });
        }
      }}
      onContextMenu={(event) => event.preventDefault()}
      onBlur={() => {
        if (useSearchStore.getState().searching) return;
        setInputValue((offset === 'start' ? locationStart : locationEnd)?.displayName ?? '');
        setLocationSearch(undefined);
      }}
      onKeyDown={handleKeyDown}
      onChange={(event) => {
        const value = event.target.value;
        if (!value) {
          const { setLocationStart, setLocationEnd, clearDirections } = useSearchStore.getState();
          const setLocation = offset === 'start' ? setLocationStart : setLocationEnd;
          setLocation(undefined);
          setLocationSearch(undefined);
          clearDirections();
        }
      }}
      className={cn({
        'border-border ring-border ring-[2px]': focused,
      })}
      focused={locationSearch?.offset === offset}
      disabled={searching}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      role="combobox"
      aria-expanded={locationSearch?.offset === offset}
      aria-controls={locationSearch?.offset === offset ? 'autocomplete-results-listbox' : undefined}
      aria-activedescendant={locationSearch?.offset === offset ? `suggestion-item-${searchIndex}` : undefined}
    />
  );
};
